import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { logger } from "@/lib/logger";

const DD_INIT_GUARD = "__QIT_DD_BROWSER_INIT__";

const isTrue = (value?: string) =>
    String(value || "").trim().toLowerCase() === "true";

const toRate = (value: string | undefined, fallback: number) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return fallback;
    if (parsed < 0) return 0;
    if (parsed > 100) return 100;
    return parsed;
};

const parseCsv = (value?: string) =>
    String(value || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);

export function initDatadogObservability(): void {
    if (typeof window === "undefined") return;

    const envEnabledRaw = String(import.meta.env.VITE_DD_ENABLED || "").trim();
    if (!isTrue(envEnabledRaw)) return;

    const guardedWindow = window as unknown as Window & Record<string, unknown>;
    if (guardedWindow[DD_INIT_GUARD]) return;

    const applicationId = String(import.meta.env.VITE_DD_APPLICATION_ID || "").trim();
    const clientToken = String(import.meta.env.VITE_DD_CLIENT_TOKEN || "").trim();
    if (!applicationId || !clientToken) {
        logger.warn("observability.datadog.missing_configuration");
        return;
    }

    const site = String(import.meta.env.VITE_DD_SITE || "us5.datadoghq.com").trim();
    const service = String(import.meta.env.VITE_DD_SERVICE || "frontend-service").trim();
    const env = String(
        import.meta.env.VITE_DD_ENV || (import.meta.env.PROD ? "prod" : "dev")
    ).trim();
    const version = String(import.meta.env.VITE_DD_VERSION || "local").trim();
    const sessionSampleRate = toRate(import.meta.env.VITE_DD_SESSION_SAMPLE_RATE, 100);
    const sessionReplaySampleRate = toRate(
        import.meta.env.VITE_DD_SESSION_REPLAY_SAMPLE_RATE,
        20
    );
    const traceSampleRate = toRate(import.meta.env.VITE_DD_TRACE_SAMPLE_RATE, 100);
    const allowedTracingUrls = parseCsv(import.meta.env.VITE_DD_ALLOWED_TRACING_URLS);

    guardedWindow[DD_INIT_GUARD] = true;

    datadogLogs.init({
        clientToken,
        site,
        service,
        env,
        version,
        sessionSampleRate,
        forwardErrorsToLogs: true,
    });

    datadogRum.init({
        applicationId,
        clientToken,
        site,
        service,
        env,
        version,
        sessionSampleRate,
        sessionReplaySampleRate,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: "mask-user-input",
        traceSampleRate,
        ...(allowedTracingUrls.length > 0
            ? { allowedTracingUrls }
            : {}),
    });

    if (sessionReplaySampleRate > 0) {
        datadogRum.startSessionReplayRecording();
    }
}
